(() => {
  const MODAL_OPEN_CLASS = 'pdp-size-chart__modal--open';
  const BODY_LOCK_CLASS = 'pdp-size-chart--locked';
  const OPEN_MODAL_HANDLER = '__openSizeChartModal';

  // Normalize various unit spellings to a canonical form
  /**
   * @param {string} [unit]
   * @returns {string}
   */
  const normalizeUnit = (unit) => {
    const u = (unit || 'cm').toLowerCase().trim();
    if (u === 'inch' || u === 'inches' || u === 'in') return 'in';
    if (u === 'cm' || u === 'centimeter' || u === 'centimeters') return 'cm';
    return u;
  };

  const MEASUREMENT_UNITS = new Set(['cm', 'in']);

  /**
   * @param {string} [columnHeader]
   * @returns {boolean}
   */
  const shouldSkipConversion = (columnHeader) => {
    const header = (columnHeader || '').toLowerCase();
    return header.includes('size');
  };

  /**
   * @param {number|string} value
   * @param {string} fromUnit
   * @param {string} toUnit
   * @param {string} columnKey
   * @returns {number|string}
   */
  const convertValue = (value, fromUnit, toUnit, columnKey) => {
    if (typeof value !== 'number') return value;
    if (shouldSkipConversion(columnKey)) return value;
    const from = normalizeUnit(fromUnit);
    const to = normalizeUnit(toUnit);

    if (!MEASUREMENT_UNITS.has(from) || !MEASUREMENT_UNITS.has(to)) {
      return value;
    }

    if (from === to) return value;
    if (from === 'cm' && to === 'in') {
      return value / 2.54;
    }
    if (from === 'in' && to === 'cm') {
      return value * 2.54;
    }
    return value;
  };

  /**
   * @param {number|string} value
   * @param {string} [unit]
   * @returns {string|number}
   */
  const formatDisplayValue = (value, unit) => {
    if (typeof value !== 'number') return value;
    const rounded = Math.round(value * 10) / 10;
    const str = rounded.toFixed(1);
    return str.endsWith('.0') ? str.slice(0, -2) : str;
  };

  const RANGE_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*([–—-])\s*(\d+(?:\.\d+)?)\s*$/;

  /**
   * @param {string} fromUnit
   * @param {string} toUnit
   * @returns {boolean}
   */
  const needsConversion = (fromUnit, toUnit) => {
    const from = normalizeUnit(fromUnit);
    const to = normalizeUnit(toUnit);
    return MEASUREMENT_UNITS.has(from) && MEASUREMENT_UNITS.has(to) && from !== to;
  };

  /**
   * @typedef {Object} SizeChartData
   * @property {string[]} columns
   * @property {Object[]} rows
   * @property {string} [unit]
   */

  /**
   * @param {HTMLElement} tableEl
   * @param {SizeChartData} data
   * @param {string} unit
   * @param {string} baseUnit
   * @param {boolean} [showUnitLabel]
   */
  const renderTable = (tableEl, data, unit, baseUnit, showUnitLabel = true) => {
    if (!tableEl || !data) return;
    const columns = Array.isArray(data.columns) ? data.columns : [];
    const rows = /** @type {Record<string, any>[]} */ (Array.isArray(data.rows) ? data.rows : []);
    if (!columns.length || !rows.length) {
      tableEl.replaceChildren();
      return;
    }

    // Expose column count to CSS for min-width calculation
    try {
      tableEl.style.setProperty('--cols', String(columns.length));
    } catch (e) { }

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');

    columns.forEach((column) => {
      const displayUnitLabel = unit === 'in' ? 'in' : 'cm';
      const originalLabel = `${column}`;
      const sanitizedLabel = originalLabel.replace(/\s*\((cm|inch|in)\)\s*$/i, '').trim();
      const headerText = sanitizedLabel || originalLabel;
      const isMeasurementColumn = !shouldSkipConversion(column) && MEASUREMENT_UNITS.has(baseUnit);
      const headerCell = document.createElement('th');
      const baseLabel = document.createElement('span');

      baseLabel.className = 'pdp-size-chart__col-label';
      baseLabel.textContent = headerText;
      headerCell.append(baseLabel);

      if (isMeasurementColumn && showUnitLabel) {
        const unitLabel = document.createElement('span');
        unitLabel.className = 'pdp-size-chart__unit-label';
        unitLabel.textContent = `(${displayUnitLabel})`;
        headerCell.append(unitLabel);
      }

      headRow.append(headerCell);
    });
    thead.append(headRow);

    const tbody = document.createElement('tbody');

    rows.forEach((row) => {
      const bodyRow = document.createElement('tr');
      columns.forEach((column) => {
        const rawValue = row[column];
        const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
        const cell = document.createElement('td');
        // A range such as "26–30" is only converted when the unit actually changes.
        const rangeMatch =
          typeof rawValue === 'string' && !shouldSkipConversion(column) && needsConversion(baseUnit, unit)
            ? rawValue.match(RANGE_PATTERN)
            : null;

        if (!Number.isNaN(numericValue) && !shouldSkipConversion(column)) {
          const converted = convertValue(numericValue, baseUnit, unit, column);
          cell.textContent = `${formatDisplayValue(converted, unit)}`;
        } else if (!Number.isNaN(numericValue) && shouldSkipConversion(column)) {
          const displayUnit = MEASUREMENT_UNITS.has(baseUnit) ? baseUnit : 'cm';
          cell.textContent = `${formatDisplayValue(numericValue, displayUnit)}`;
        } else if (rangeMatch) {
          const low = formatDisplayValue(convertValue(Number(rangeMatch[1]), baseUnit, unit, column), unit);
          const high = formatDisplayValue(convertValue(Number(rangeMatch[3]), baseUnit, unit, column), unit);
          cell.textContent = `${low}${rangeMatch[2]}${high}`;
        } else {
          cell.textContent = rawValue == null ? '' : `${rawValue}`;
        }

        bodyRow.append(cell);
      });
      tbody.append(bodyRow);
    });

    tableEl.replaceChildren(thead, tbody);
  };

  /**
   * Picked size inside a product information section (the first row cell holds the size name).
   * @param {ParentNode} scope
   * @returns {string}
   */
  const getPickedSize = (scope) => {
    const picked = scope.querySelector('[data-option-role="size"] input:checked, [data-option-role="size"] select');
    return picked instanceof HTMLInputElement || picked instanceof HTMLSelectElement ? picked.value.trim() : '';
  };

  /**
   * @param {HTMLElement | null} tableEl
   * @param {string} value
   */
  const markRows = (tableEl, value) => {
    if (!tableEl) return;
    tableEl.querySelectorAll('tbody tr').forEach((row) => {
      const firstCell = /** @type {HTMLTableRowElement} */ (row).cells[0];
      row.classList.toggle('is-current', Boolean(value) && firstCell?.textContent?.trim() === value);
    });
  };

  // Inline panel (blocks/size-chart.liquid): a disclosure under the size picker on the product page.
  // It lives outside <variant-picker>, which morphs on every variant change, so all wiring is delegated.

  /**
   * @param {Element} trigger
   * @returns {HTMLElement | null}
   */
  const getControlledPanel = (trigger) => {
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    return panel instanceof HTMLElement && panel.hasAttribute('data-size-chart-panel') ? panel : null;
  };

  // Keyed by the raw JSON too: a main morph (colour sibling) keeps the element but swaps the product.
  /** @type {WeakMap<HTMLElement, { raw: string, data: SizeChartData | null }>} */
  const panelData = new WeakMap();

  /**
   * @param {HTMLElement} panel
   * @returns {SizeChartData | null}
   */
  const getPanelData = (panel) => {
    const raw = panel.getAttribute('data-size-chart') || '';
    const cached = panelData.get(panel);
    if (cached && cached.raw === raw) return cached.data;
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('Invalid size chart JSON', error);
    }
    panelData.set(panel, { raw, data });
    return data;
  };

  /**
   * @param {HTMLElement} panel
   */
  const markPanelRow = (panel) => {
    markRows(panel.querySelector('[data-size-chart-table]'), getPickedSize(panel.closest('.product-information') || document));
  };

  // Shows the right-edge fade only while more columns are hidden; a scrollable table becomes keyboard focusable.
  /**
   * @param {HTMLElement} panel
   */
  const updatePanelFade = (panel) => {
    const frame = panel.querySelector('[data-size-chart-frame]');
    const scroller = panel.querySelector('[data-size-chart-scroll]');
    if (!frame || !scroller) return;
    const max = scroller.scrollWidth - scroller.clientWidth;
    frame.toggleAttribute('data-fade-end', max > 2 && Math.abs(scroller.scrollLeft) < max - 2);
    if (max > 2) scroller.setAttribute('tabindex', '0');
    else scroller.removeAttribute('tabindex');
  };

  /**
   * @param {HTMLElement} panel
   * @param {boolean} open
   */
  const setPanelOpen = (panel, open) => {
    panel.classList.toggle('is-open', open);
    panel.inert = !open;
    document.querySelectorAll(`[aria-controls="${CSS.escape(panel.id)}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(open));
    });
    if (open) {
      markPanelRow(panel);
      updatePanelFade(panel);
    }
  };

  /**
   * Re-syncs triggers after the picker morph resets their attributes. A trigger whose panel is
   * missing (no size chart block in the template) goes back to opening the modal.
   * @param {Document | HTMLElement} [context]
   */
  const syncPanelTriggers = (context = document) => {
    context.querySelectorAll('[data-size-chart-trigger][aria-controls]').forEach((trigger) => {
      const panel = getControlledPanel(trigger);
      if (panel) {
        trigger.setAttribute('aria-expanded', String(panel.classList.contains('is-open')));
        markPanelRow(panel);
      } else {
        trigger.removeAttribute('aria-controls');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-size-chart-panel] [data-size-chart-unit]');
    const panel = button?.closest('[data-size-chart-panel]');
    if (!(button instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
    const data = getPanelData(panel);
    const toggle = panel.querySelector('[data-size-chart-toggle]');
    const tableEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-size-chart-table]'));
    const unit = normalizeUnit(button.dataset.sizeChartUnit);
    if (!data || !(toggle instanceof HTMLElement) || !tableEl || toggle.dataset.activeUnit === unit) return;
    toggle.dataset.activeUnit = unit;
    toggle.querySelectorAll('[data-size-chart-unit]').forEach((btn) => {
      const isActive = normalizeUnit(/** @type {HTMLElement} */ (btn).dataset.sizeChartUnit) === unit;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });
    renderTable(tableEl, data, unit, normalizeUnit(data.unit), true);
    markPanelRow(panel);
    updatePanelFade(panel);
  });

  // Highlight the picked size straight away; the picker morph that follows is re-synced on size-chart:refresh.
  // Capture phase, so the picker's own change handling cannot stop it.
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
      if (!input.closest('[data-option-role="size"]')) return;
      const scope = input.closest('.product-information');
      if (!scope) return;
      scope.querySelectorAll('[data-size-chart-panel]').forEach((panel) => {
        markRows(panel.querySelector('[data-size-chart-table]'), input.value.trim());
      });
    },
    true
  );

  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = event.target instanceof Element ? event.target.closest('[data-size-chart-scroll]') : null;
      const panel = scroller?.closest('[data-size-chart-panel]');
      if (panel instanceof HTMLElement) updatePanelFade(panel);
    },
    { capture: true, passive: true }
  );

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-size-chart-panel].is-open').forEach((panel) => {
      updatePanelFade(/** @type {HTMLElement} */ (panel));
    });
  });

  // Theme editor: selecting the size chart block shows its panel.
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target instanceof Element ? event.target.querySelector('[data-size-chart-panel]') : null;
    if (panel instanceof HTMLElement) setPanelOpen(panel, true);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const panel = event.target instanceof Element ? event.target.querySelector('[data-size-chart-panel]') : null;
    if (panel instanceof HTMLElement) setPanelOpen(panel, false);
  });

  /**
   * @param {HTMLElement} root
   */
  const initSizeChart = (root) => {
    if (!root || root.__sizeChartInitialized === true) return;

    const trigger = /** @type {HTMLElement} */ (root.querySelector('[data-size-chart-trigger]'));
    const modal = /** @type {HTMLElement} */ (root.querySelector('[data-size-chart-modal]'));
    const content = /** @type {HTMLElement} */ (modal?.querySelector('[data-size-chart-content]'));
    if (!trigger || !modal || !content) return;
    // The trigger expands the inline panel instead; the modal stays as the fallback only.
    if (getControlledPanel(trigger)) return;

    trigger.setAttribute('aria-expanded', trigger.getAttribute('aria-expanded') || 'false');

    const tableData = content.getAttribute('data-size-chart');
    const tableEl = /** @type {HTMLElement} */ (content.querySelector('[data-size-chart-table]'));
    const unitButtons = /** @type {NodeListOf<HTMLElement>} */ (content.querySelectorAll('[data-size-chart-unit]'));
    const slider = /** @type {HTMLElement} */ (content.querySelector('[data-size-chart-slider]'));
    const toggle = /** @type {HTMLElement} */ (content.querySelector('[data-size-chart-toggle]'));
    let parsedData = null;

    if (tableData) {
      try {
        parsedData = JSON.parse(tableData);
      } catch (error) {
        console.warn('Invalid size chart JSON', error);
      }
    }

    const baseUnit = normalizeUnit(parsedData?.unit);
    const defaultUnitAttr = content.getAttribute('data-size-chart-default-unit');
    let currentUnit = defaultUnitAttr ? normalizeUnit(defaultUnitAttr) : baseUnit;

    const updateSliderPosition = () => {
      if (!slider || !toggle || !unitButtons.length) return;
      const buttons = Array.from(unitButtons);
      const activeIndex = Math.max(buttons.findIndex((btn) => btn.classList.contains('is-active')), 0);
      const count = Math.max(buttons.length, 2);
      const widthPercent = 100 / count;
      slider.style.width = `${widthPercent}%`;
      slider.style.transform = `translateX(${activeIndex * 100}%)`;
    };

    /**
     * @param {string} unit
     */
    const setActiveUnitButton = (unit) => {
      unitButtons.forEach((btn) => {
        const btnUnit = normalizeUnit(btn.dataset.sizeChartUnit);
        const isActive = btnUnit === unit;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      });
      if (toggle) {
        toggle.dataset.activeUnit = unit;
      }
      requestAnimationFrame(updateSliderPosition);
    };

    const showUnitLabel = Boolean(toggle);

    if (parsedData && tableEl) {
      renderTable(tableEl, parsedData, currentUnit, baseUnit, showUnitLabel);
      setActiveUnitButton(currentUnit);
    }

    if (parsedData && unitButtons.length) {
      unitButtons.forEach((btn) => {
        const targetUnit = normalizeUnit(btn.dataset.sizeChartUnit);
        if (!targetUnit) return;
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          if (targetUnit === currentUnit) return;
          currentUnit = normalizeUnit(targetUnit);
          setActiveUnitButton(currentUnit);
          renderTable(tableEl, parsedData, currentUnit, baseUnit, showUnitLabel);
          markCurrentRow();
        });
      });
    }

    // Highlight the row of the size picked in the variant picker.
    const markCurrentRow = () => {
      markRows(tableEl, getPickedSize(root.closest('.product-information') || document));
    };

    let toggleObserver = null;
    /** @type {Array<() => void>} */
    const orientationCleanup = [];
    const handleOrientationChange = () => requestAnimationFrame(updateSliderPosition);

    if (slider && unitButtons.length) {
      const updateSliderWithFrame = () => requestAnimationFrame(updateSliderPosition);
      const handleResize = () => updateSliderWithFrame();
      window.addEventListener('resize', handleResize);

      if (typeof ResizeObserver === 'function' && toggle) {
        toggleObserver = new ResizeObserver(() => updateSliderWithFrame());
        toggleObserver.observe(toggle);
      }

      if (typeof window.matchMedia === 'function') {
        const mediaQuery = window.matchMedia('(orientation: portrait)');
        if (typeof mediaQuery.addEventListener === 'function') {
          mediaQuery.addEventListener('change', handleOrientationChange);
          orientationCleanup.push(() => mediaQuery.removeEventListener('change', handleOrientationChange));
        } else if (typeof mediaQuery.addListener === 'function') {
          mediaQuery.addListener(handleOrientationChange);
          orientationCleanup.push(() => mediaQuery.removeListener(handleOrientationChange));
        }
      }

      requestAnimationFrame(updateSliderPosition);
    }

    /** @type {HTMLElement | null} */
    let lastFocused = null;

    const getFocusableElements = () =>
      Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        const rect = element.getBoundingClientRect();
        const styles = window.getComputedStyle(element);
        return rect.width > 0 && rect.height > 0 && styles.visibility !== 'hidden' && styles.display !== 'none';
      });

    const closeModal = () => {
      // Remove open class to trigger slide-down animation
      modal.classList.remove(MODAL_OPEN_CLASS);

      // Immediately unlock page and restore control (don't wait for animation)
      document.body.classList.remove(BODY_LOCK_CLASS);
      trigger.setAttribute('aria-expanded', 'false');
      root.classList.remove('pdp-size-chart--open');
      window.removeEventListener('keydown', handleKeydown);

      // Wait for animation to complete before hiding (faster cleanup)
      setTimeout(() => {
        modal.setAttribute('hidden', 'true');
      }, 250);

      if (toggleObserver) {
        toggleObserver.disconnect();
      }
      if (orientationCleanup.length) {
        orientationCleanup.forEach((cleanup) => {
          try {
            cleanup();
          } catch (error) {
            console.warn('Orientation listener cleanup failed', error);
          }
        });
        orientationCleanup.length = 0;
      }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    };

    /**
     * @param {KeyboardEvent} event
     */
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        modal.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus({ preventScroll: true });
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus({ preventScroll: true });
      }
    };

    /**
     * @param {Event} event
     */
    const openModal = (event) => {
      event.preventDefault();
      if (!modal.hasAttribute('hidden') && modal.classList.contains(MODAL_OPEN_CLASS)) return;
      lastFocused = /** @type {HTMLElement} */ (document.activeElement);
      markCurrentRow();
      // The sticky product column creates a stacking context under the header; lift the sheet out of it.
      if (modal.parentElement !== document.body) document.body.append(modal);
      modal.removeAttribute('hidden');

      // Use requestAnimationFrame to ensure CSS animation triggers
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          modal.classList.add(MODAL_OPEN_CLASS);
        });
      });

      document.body.classList.add(BODY_LOCK_CLASS);
      trigger.setAttribute('aria-expanded', 'true');
      root.classList.add('pdp-size-chart--open');
      window.addEventListener('keydown', handleKeydown);
      if (toggleObserver && toggle) {
        toggleObserver.observe(toggle);
      }
      const focusTarget = /** @type {HTMLElement} */ (modal.querySelector('.pdp-size-chart__close') || modal);
      focusTarget.focus({ preventScroll: true });
      requestAnimationFrame(updateSliderPosition);
    };

    root[OPEN_MODAL_HANDLER] = openModal;
    trigger.addEventListener('click', openModal);

    modal.querySelectorAll('[data-size-chart-close]').forEach((/** @type {HTMLElement} */ el) => {
      el.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal();
      }
    });

    root.dataset.sizeChartInitialized = 'true';
    root.__sizeChartInitialized = true;
  };

  /**
   * @param {Document | HTMLElement} [context]
   */
  const initAllSizeCharts = (context = document) => {
    syncPanelTriggers(context);
    document.querySelectorAll('[data-size-chart-panel]').forEach((panel) => {
      updatePanelFade(/** @type {HTMLElement} */ (panel));
    });
    const roots = context.querySelectorAll('[data-size-chart-root]');
    roots.forEach((root) => initSizeChart(/** @type {HTMLElement} */(root)));
  };

  window.initSizeCharts = initAllSizeCharts;

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-size-chart-trigger]');
      if (!trigger) return;

      const panel = getControlledPanel(trigger);
      if (panel) {
        event.preventDefault();
        event.stopPropagation();
        setPanelOpen(panel, !panel.classList.contains('is-open'));
        return;
      }

      const root = trigger.closest('[data-size-chart-root]');
      if (!(root instanceof HTMLElement)) return;

      initSizeChart(root);

      if (typeof root[OPEN_MODAL_HANDLER] !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      root[OPEN_MODAL_HANDLER](event);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllSizeCharts(document));
  } else {
    initAllSizeCharts(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllSizeCharts(/** @type {HTMLElement} */(event.target));
  });

  document.addEventListener('size-chart:refresh', (event) => {
    const target = event.target instanceof HTMLElement ? event.target : document;
    initAllSizeCharts(target);
  });
})();
